import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ListGroup, Card, Form } from 'react-bootstrap';
import { collection, addDoc, onSnapshot, query, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/firebase';
import { AppContext } from '../../../../Context';
import NavbarGlobal from '../../../NavbarGlobal';
import ReaderTool from '../../../../components/ReaderTool/ReaderTool';
import Swal from 'sweetalert2';
import { registrarErrorEnvioEjercicio } from '../../../../utils/registroErroresEjercicios';
import './../../../DetalleMaterialTeorico.scss';
import '../../../../theme/modal-consistency.scss';
import '../../../../theme/letter-format.scss';
import './LecturasCarga.scss';

const DetalleLecturasAlumnos = () => {
    const { nivel } = useParams();
    const navigate = useNavigate();
    const { currentUser, teacher } = useContext(AppContext);

    const [lecturaSeleccionada, setLecturaSeleccionada] = useState(null);
    const [lecturas, setLecturas] = useState([]);
    const [estadoCarga, setEstadoCarga] = useState('cargando');
    const [intentoCarga, setIntentoCarga] = useState(0);
    const [respuestasLocales, setRespuestasLocales] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [freeResponseText, setFreeResponseText] = useState('');
    const [guardando, setGuardando] = useState(false);

    const documentColumnClass = lecturaSeleccionada?.columnas === 2 || lecturaSeleccionada?.columnas === '2'
        ? 'lesson-document--two-columns'
        : 'lesson-document--one-column';

    useEffect(() => {
        let unsubscribe;
        let activo = true;
        let revision = 0;
        setEstadoCarga('cargando');
        setLecturas([]);
        setLecturaSeleccionada(null);
        setSearchText('');
        const mostrarError = (error) => {
            console.error('Error al obtener lecturas:', error);
            if (activo) setEstadoCarga('error');
        };
        const load = async () => {
            try {
                if (teacher || currentUser?.isMaster) {
                    const qLecturas = query(collection(db, 'lecturasAdicionales'));
                    unsubscribe = onSnapshot(qLecturas, (snapshot) => {
                        if (!activo) return;
                        const lecturasActualizadas = snapshot.docs
                            .map(docu => ({ id: docu.id, ...docu.data(), level: String(docu.data().level || '') }))
                            .filter(lectura => {
                                const lecturaNivel = Number.parseInt(lectura.level.replace('Nivel ', ''), 10);
                                const nivelActual = Number.parseInt(nivel, 10);
                                return lecturaNivel === nivelActual;
                            })
                            .sort((a, b) => (a.posicion || Number.MAX_SAFE_INTEGER) - (b.posicion || Number.MAX_SAFE_INTEGER));
                        setLecturas(lecturasActualizadas);
                        setEstadoCarga('listo');
                    }, mostrarError);
                    return;
                }
                if (!currentUser?.uid) {
                    setLecturas([]);
                    setEstadoCarga('listo');
                    return;
                }
                const alumnoRef = doc(db, 'alumnosLecturas', currentUser.uid);
                unsubscribe = onSnapshot(alumnoRef, async (snap) => {
                    const revisionActual = ++revision;
                    try {
                        if (!activo) return;
                        if (!snap.exists()) {
                            setLecturas([]);
                            setEstadoCarga('listo');
                            return;
                        }
                        const asignadas = snap.data().lecturasAsignadas || [];
                        const asignadasNivel = asignadas.filter(l => {
                            const normalizedLvl = l.level?.toString().replace('Nivel ', '') || '';
                            return Number.parseInt(normalizedLvl, 10) === Number.parseInt(nivel, 10);
                        });
                        const documentos = await Promise.all(asignadasNivel
                            .filter(item => item?.id)
                            .map(item => getDoc(doc(db, 'lecturasAdicionales', item.id))));
                        if (!activo || revisionActual !== revision) return;
                        const results = [];
                        for (const ld of documentos) {
                            if (ld.exists()) {
                                const data = ld.data();
                                results.push({ id: ld.id, ...data, level: String(data.level || '') });
                            }
                        }
                        results.sort((a, b) => (a.posicion || Number.MAX_SAFE_INTEGER) - (b.posicion || Number.MAX_SAFE_INTEGER));
                        setLecturas(results);
                        setEstadoCarga('listo');
                    } catch (err) {
                        if (revisionActual === revision) mostrarError(err);
                    }
                }, mostrarError);
            } catch (e) {
                mostrarError(e);
            }
        };
        load();
        return () => {
            activo = false;
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, [currentUser?.uid, currentUser?.isMaster, teacher, nivel, intentoCarga]);

    const handleLecturaSeleccionada = (lectura) => {
        // Primero desplazamos la página al inicio
        window.scrollTo(0, 0);
        // Luego actualizamos el estado con un pequeño retraso para asegurar que el scroll se complete
        setTimeout(() => {
            setLecturaSeleccionada(lectura);
            setCurrentStep(0);
            setSelectedOption(null);
            setFreeResponseText('');
            setRespuestasLocales([]);
        }, 10);
    };

    const prepararRespuestas = () => {
        const totalSteps = lecturaSeleccionada?.evaluations?.length || 0;
        let respuesta;
        if (currentStep >= 1 && currentStep <= totalSteps) {
            if (!selectedOption) {
                Swal.fire({ icon: 'warning', title: 'Atención', text: 'Por favor seleccione una respuesta.' });
                return null;
            }
            respuesta = { step: currentStep, selectedOption };
        } else if (currentStep === totalSteps + 1 && lecturaSeleccionada?.freeResponseEvaluation) {
            if (!freeResponseText.trim()) {
                Swal.fire({ icon: 'warning', title: 'Atención', text: 'Por favor complete el campo de texto.' });
                return null;
            }
            respuesta = { step: currentStep, freeResponseText };
        }
        return respuesta
            ? [...respuestasLocales.filter(item => item.step !== currentStep), respuesta].sort((a, b) => a.step - b.step)
            : respuestasLocales;
    };

    const irAlPaso = (paso, respuestas = respuestasLocales) => {
        const guardada = respuestas.find(item => item.step === paso);
        setSelectedOption(guardada?.selectedOption || null);
        setFreeResponseText(guardada?.freeResponseText || '');
        setCurrentStep(paso);
        window.scrollTo(0, 0);
    };

    const handleFinalSave = async (respuestas) => {
        if (guardando) return;
        if (!currentUser?.uid) {
            await Swal.fire({ icon: 'warning', title: 'Sesión no disponible', text: 'Vuelve a iniciar sesión para guardar tus respuestas.' });
            return;
        }
        setGuardando(true);
        const response = {
            lecturaId: lecturaSeleccionada.id,
            userid: currentUser.uid,
            respuestas,
            completedAt: serverTimestamp(),
        };

        try {
            await addDoc(collection(db, 'respuestaLecturasAdicionales'), response);

            Swal.fire({
                icon: 'success',
                title: 'Respuestas guardadas correctamente',
                confirmButtonText: 'Aceptar',
            }).then(() => {
                setCurrentStep(0);
                setRespuestasLocales([]);
                setSelectedOption(null);
                setFreeResponseText('');
            });
        } catch (error) {
            console.error("Error al guardar las respuestas:", error);
            void registrarErrorEnvioEjercicio({
                alumnoId: currentUser?.uid,
                correoAlumno: currentUser?.email,
                nombreAlumno: currentUser?.nombre || currentUser?.displayName,
                lectura: lecturaSeleccionada?.nombre,
                lecturaId: lecturaSeleccionada?.id,
                nivel: `Nivel ${nivel}`,
                modulo: 'Lecturas adicionales',
                ejercicio: currentStep === (lecturaSeleccionada?.evaluations?.length || 0) + 1
                    ? lecturaSeleccionada?.freeResponseEvaluation?.title || 'Respuesta libre'
                    : lecturaSeleccionada?.evaluations?.[currentStep - 1]?.title,
            }, error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo guardar las respuestas. Inténtalo nuevamente. Tus respuestas se conservan.',
            });
        } finally {
            setGuardando(false);
        }
    };

    const handleNextStep = () => {
        if (guardando) return;
        const respuestas = prepararRespuestas();
        if (!respuestas) return;
        setRespuestasLocales(respuestas);
        const totalSteps = lecturaSeleccionada?.evaluations?.length || 0;
        const ultimoPaso = totalSteps + (lecturaSeleccionada?.freeResponseEvaluation ? 1 : 0);
        if (currentStep < ultimoPaso) {
            irAlPaso(currentStep + 1, respuestas);
        } else {
            handleFinalSave(respuestas);
        }
    };

    const handlePreviousStep = () => {
        if (guardando) return;
        const totalSteps = lecturaSeleccionada?.evaluations?.length || 0;
        const respuesta = currentStep <= totalSteps
            ? { step: currentStep, selectedOption }
            : { step: currentStep, freeResponseText };
        const respuestas = [...respuestasLocales.filter(item => item.step !== currentStep), respuesta];
        setRespuestasLocales(respuestas);
        irAlPaso(Math.max(currentStep - 1, 0), respuestas);
    };

    const lecturasFiltradas = lecturas.filter(lectura =>
        (lectura.nombre || '').toLowerCase().includes(searchText.trim().toLowerCase())
    );

    const renderStep = () => {
        const totalSteps = lecturaSeleccionada?.evaluations?.length || 0;

        if (currentStep === 0) {
            return (
                <article className="lectura-content lesson-reader">
                    <header className="content-header">
                        <span className="content-level">Nivel {nivel}</span>
                        <h1 className="content-title">{lecturaSeleccionada?.nombre}</h1>
                    </header>

                    <ReaderTool
                        columnClass={documentColumnClass}
                        html={lecturaSeleccionada?.descripcion || ''}
                        storageKey="lecturas-adicionales"
                    />

                    <footer className="content-actions">
                        <Button variant="primary" disabled={guardando} onClick={handleNextStep}>Iniciar Evaluación</Button>
                    </footer>
                </article>
            );
        }

        if (currentStep <= totalSteps && currentStep > 0) {
            const evaluation = lecturaSeleccionada.evaluations[currentStep - 1];
            return (
                <Card className="evaluation-card">
                    <Card.Body>
                        <h5 className="evaluation-title">{evaluation.title}</h5>
                        <div className="evaluation-options">
                            {evaluation.answers.map((answer, index) => (
                                answer && (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        id={`lectura-${lecturaSeleccionada.id}-${currentStep}-${index}`}
                                        disabled={guardando}
                                        label={answer}
                                        name={`evaluation-${currentStep}`}
                                        value={answer}
                                        checked={selectedOption === answer}
                                        onChange={e => setSelectedOption(e.target.value)}
                                        className="evaluation-option"
                                    />
                                )
                            ))}
                        </div>
                        <div className="step-buttons">
                            <Button variant="secondary" onClick={handlePreviousStep} disabled={guardando || currentStep === 1}>
                                Atrás
                            </Button>
                            <Button variant="primary" disabled={guardando} onClick={handleNextStep}>
                                {currentStep === totalSteps && !lecturaSeleccionada.freeResponseEvaluation ? 'Guardar Respuestas' : 'Siguiente'}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            );
        }

        if (lecturaSeleccionada?.freeResponseEvaluation && currentStep === totalSteps + 1) {
            return (
                <Card className="free-response-card">
                    <Card.Body>
                        <h5 className="evaluation-title">{lecturaSeleccionada.freeResponseEvaluation.title}</h5>
                        <Form.Control
                            as="textarea"
                            disabled={guardando}
                            rows={5}
                            value={freeResponseText}
                            onChange={e => setFreeResponseText(e.target.value)}
                            placeholder="Escribe tu respuesta aquí..."
                            className="free-response-textarea"
                        />
                        <div className="step-buttons">
                            <Button variant="secondary" disabled={guardando} onClick={handlePreviousStep}>
                                Atrás
                            </Button>
                            <Button variant="success" disabled={guardando} onClick={handleNextStep}>
                                Guardar Respuestas
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            );
        }

        return null;
    };

    return (
        <div className="detalle-container">
            <NavbarGlobal
                title={`Lecturas para el nivel ${nivel}`}
                showBackButton={false}
            />
                <div className="detalle-content lecturas-content">
                    <Button
                        variant="outline-secondary"
                        className="back-to-list-btn"
                        onClick={() => navigate('/ViewLecturasAlumnos')}
                    >
                        ← Volver a niveles
                    </Button>
                    {!lecturaSeleccionada ? (
                        // Vista inicial: solo mostrar la lista de lecturas a pantalla completa
                        <div className="lecturas-list-view">
                            <div className="lecturas-search-container">
                                <input
                                    type="text"
                                    className="detalle-search-bar lecturas-search"
                                    placeholder="Buscar lectura..."
                                    aria-label="Buscar lectura"
                                    disabled={estadoCarga !== 'listo'}
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                />
                            </div>
                            <div className="lecturas-list-container">
                                <h6 className="lecturas-list-title">Lecturas Disponibles</h6>
                                {estadoCarga === 'cargando' ? (
                                    <div role="status" aria-live="polite" aria-busy="true">
                                        <span className="visually-hidden">Cargando lecturas del nivel {nivel}…</span>
                                        <div className="lecturas-list" aria-hidden="true">
                                            {[0, 1, 2].map(item => (
                                                <div key={item} className="lectura-item lectura-skeleton">
                                                    <span className="lectura-skeleton__line lectura-skeleton__line--title" />
                                                    <span className="lectura-skeleton__line" />
                                                    <span className="lectura-skeleton__line" />
                                                    <span className="lectura-skeleton__line lectura-skeleton__line--meta" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : estadoCarga === 'error' ? (
                                    <div className="text-center py-4" role="alert">
                                        <h6>No pudimos cargar las lecturas.</h6>
                                        <p>Revisa tu conexión y vuelve a intentarlo.</p>
                                        <Button variant="outline-primary" onClick={() => setIntentoCarga(intento => intento + 1)}>
                                            Reintentar
                                        </Button>
                                    </div>
                                ) : lecturasFiltradas.length === 0 ? (
                                    <div className="text-center py-4">
                                        <h6 className="text-muted">{lecturas.length > 0
                                            ? 'No encontramos lecturas con esa búsqueda.'
                                            : teacher || currentUser?.isMaster
                                                ? 'Todavía no hay lecturas en este nivel.'
                                                : 'No tienes lecturas asignadas en este nivel.'}</h6>
                                        {lecturas.length > 0 && (
                                            <Button variant="outline-secondary" onClick={() => setSearchText('')}>Limpiar búsqueda</Button>
                                        )}
                                    </div>
                                ) : (
                                    <ListGroup className="lecturas-list">
                                        {lecturasFiltradas.map(lectura => {
                                            const plain = (lectura?.descripcion || '')
                                              .replace(/<[^>]*>/g, ' ')
                                              .replace(/\s+/g, ' ')
                                              .trim();
                                            const wordCount = plain ? plain.split(' ').length : 0;
                                            return (
                                                <button
                                                    type="button"
                                                    key={lectura.id}
                                                    className="lectura-item"
                                                    onClick={() => handleLecturaSeleccionada(lectura)}
                                                >
                                                    <div className="lectura-item-content">
                                                        <h6 className="lectura-item-title">{lectura.nombre}</h6>
                                                        <p className="lectura-item-description">
                                                            {lectura.descripcion
                                                                ? lectura.descripcion.replace(/<[^>]*>/g, '').substring(0, 100) + '...'
                                                                : 'Sin descripción disponible'}
                                                        </p>
                                                        <div className="lectura-item-meta">
                                                            <span className="lectura-level">Nivel {lectura.level}</span>
                                                            <span className="lectura-questions">
                                                                {wordCount} {wordCount === 1 ? 'palabra' : 'palabras'}
                                                            </span>
                                                            {lectura.evaluations && (
                                                                <span className="lectura-questions">
                                                                    {lectura.evaluations.length} preguntas
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </ListGroup>
                                )}
                            </div>
                        </div>
                    ) : (
                        // Vista de lectura seleccionada: mostrar contenido con botón para volver a la lista
                        <div className="lecturas-detail-view">
                            <Button 
                                variant="outline-secondary" 
                                className="back-to-list-btn"
                                onClick={() => {
                                    // Primero desplazamos la página al inicio
                                    window.scrollTo(0, 0);
                                    // Luego actualizamos el estado con un pequeño retraso
                                    setTimeout(() => {
                                        setLecturaSeleccionada(null);
                                    }, 10);
                                }}
                            >
                                ← Volver a la lista
                            </Button>
                            <div className="lecturas-content-wrapper">
                                {renderStep()}
                            </div>
                        </div>
                    )}
                </div>
        </div>
    );
};

export { DetalleLecturasAlumnos };
